import { useEffect, useMemo, useState } from 'react';

import { MerchantSearch, useListMerchants } from '@givebacks/api/offers';
import { useBreakPoints } from 'core';
import { useSearchParams } from 'react-router-dom';
import { Center, Loader, NoResults, PaginationForSearch, Stack } from 'ui';

import { rewardSortValidation } from '../rewardFilterValidation';
import { OnlineMerchantSearchHeader } from './OnlineMerchantSearchHeader/OnlineMerchantSearchHeader';
import { MerchantCardWrapper as MerchantCard } from './components/MerchantCard';

type OnlineMerchantSearchPaginationProps = {
  pageSize: number;
  setPageSize: (value: number) => void;
  page: number;
  setPage: (value: number) => void;
  total: number;
};

const OnlineMerchantSearchPagination = ({
  pageSize,
  setPageSize,
  page,
  setPage,
  total,
}: OnlineMerchantSearchPaginationProps) => {
  const paginationValue = page + 1;
  const pageTotal = useMemo(() => Math.ceil(total / pageSize), [total, pageSize]);

  return (
    <PaginationForSearch
      selectProps={{
        defaultValue: String(pageSize),
        onChange(value) {
          setPageSize(value);
        },
      }}
      paginationProps={{
        total: pageTotal,
        value: paginationValue,
        onChange(nextPage) {
          setPage(nextPage - 1);
        },
      }}
    />
  );
};

const SearchData = () => {
  const [searchParams] = useSearchParams();
  const searchName = searchParams.get('name') ?? '';
  const searchOrder = rewardSortValidation.parse(searchParams.get('order'));
  const categoryId = searchParams.get('category') ?? '';

  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(0);

  const searches: MerchantSearch = useMemo(() => {
    const descendingSortFields = ['promotional_amount', 'created_at'];

    const search: MerchantSearch = {
      order: {
        field: 'popularity',
        direction: 'asc',
      },
      searches: {
        name: { value: searchName, by: 'in' },
        online: { value: true, by: 'in' },
      },
      options: {
        total: true,
      },
    };

    if (searchOrder) {
      Object.assign(search, {
        order: {
          field: searchOrder,
          direction: descendingSortFields.includes(searchOrder) ? 'desc' : 'asc',
        },
      });
    }

    // if sort is promotional_amount, add search for promotional_type = percentage
    if (searchOrder === 'promotional_amount') {
      Object.assign(search.searches ?? {}, { promotional_type: { value: 'percentage' } });
    }

    if (categoryId !== 'all' && categoryId) {
      Object.assign(search.searches ?? {}, {
        'categories.uuid': {
          value: categoryId,
        },
      });
    }
    return search;
  }, [categoryId, searchName, searchOrder]);

  const { data, isLoading, isFetching } = useListMerchants({
    queryOptions: {
      ...searches,
      join: 'AND',
    },
    pagination: {
      limit: pageSize,
      page,
    },
  });

  const onlineMerchants = useMemo(() => {
    return data?.merchants.map((merchant) => (
      <MerchantCard key={merchant.uuid} merchant={merchant} />
    ));
  }, [data?.merchants]);

  // Since we are using search params to search, we have to be reactive when resetting page count when search params change
  useEffect(() => {
    setPage(0);
  }, [searchName, categoryId]);

  return (
    <Stack gap="md">
      {!isLoading && !isFetching && !data?.merchants.length && <NoResults />}
      {isLoading || isFetching ? (
        <Center>
          <Loader />
        </Center>
      ) : (
        <Stack gap={0}>{onlineMerchants}</Stack>
      )}
      <OnlineMerchantSearchPagination
        pageSize={pageSize}
        setPageSize={setPageSize}
        page={page}
        setPage={setPage}
        total={data?.meta.total ?? 0}
      />
    </Stack>
  );
};

const OnlineSearch = () => {
  const { isSmallScreen } = useBreakPoints();

  return (
    <Stack flex={1} p="md" gap={isSmallScreen ? 'md' : 'lg'}>
      <OnlineMerchantSearchHeader />
      <SearchData />
    </Stack>
  );
};

export { OnlineSearch };
